import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Empty, Spin, Button, message } from 'antd';
import {
  SearchOutlined,
  FileTextOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  getKnowledgeBaseList,
  type KnowledgeBaseItem,
} from '../../../../api';

export interface KnowledgeBaseSelection {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  value?: string;
  onCancel: () => void;
  onOk: (selection: KnowledgeBaseSelection) => void;
}

/** 格式化数字：大于 1 万用 w 缩写 */
function formatCount(n?: number) {
  if (n == null) return '-';
  if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
  return String(n);
}

/** 将秒级时间戳格式化为 YYYY-MM-DD */
function formatDate(ts?: number) {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const KnowledgeBasePickerModal: React.FC<Props> = ({
  open,
  value,
  onCancel,
  onOk,
}) => {
  const [keyword, setKeyword] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const { data: list = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['knowledge-base-list'],
    queryFn: getKnowledgeBaseList,
    enabled: open,
  });

  /** 搜索过滤：按 name + description 模糊匹配 */
  const visibleList = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return list;
    return list.filter(item => {
      const name = (item.name || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      return name.includes(kw) || desc.includes(kw);
    });
  }, [list, keyword]);

  /** 弹窗打开时，初始化选中 */
  useEffect(() => {
    if (!open) return;
    setSelectedId(value ?? null);
  }, [open, value]);

  const handleConfirm = () => {
    const item = list.find(i => i.id === selectedId);
    if (!item) {
      messageApi.warning('请选择一个知识库');
      return;
    }
    onOk({ id: item.id, name: item.name });
  };

  const renderCard = (item: KnowledgeBaseItem) => {
    const selected = item.id === selectedId;
    return (
      <div
        key={item.id}
        role="button"
        tabIndex={0}
        onClick={() => setSelectedId(item.id)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedId(item.id);
          }
        }}
        className={[
          'relative cursor-pointer rounded-md border px-3 py-2.5 transition-colors',
          selected
            ? 'border-[#2094f0] bg-[#f0f9ff]'
            : 'border-[#e5e7eb] bg-white hover:border-[#2094f0]',
        ].join(' ')}
      >
        {selected && (
          <CheckCircleFilled className="absolute right-2 top-2 text-[#2094f0]" />
        )}
        <div className="flex items-start gap-2">
          <FileTextOutlined className="mt-1 shrink-0 text-[#2094f0]" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-[#111827]">
              {item.name}
            </div>
            {item.description && (
              <div className="mt-0.5 line-clamp-2 text-xs text-[#6b7280]">
                {item.description}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#9ca3af]">
              <span>文档 {formatCount(item.document_count)}</span>
              <span>字数 {formatCount(item.word_count)}</span>
              <span>更新 {formatDate(item.updated_at)}</span>
              {item.indexing_technique && (
                <span className="rounded bg-[#f3f4f6] px-1.5 py-0.5 text-[#6b7280]">
                  {item.indexing_technique}
                </span>
              )}
              {item.embedding_available === false && (
                <span className="rounded bg-[#fef2f2] px-1.5 py-0.5 text-[#ef4444]">
                  embedding 不可用
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const selectedItem = list.find(i => i.id === selectedId);

  return (
    <Modal
      title="选择知识库"
      open={open}
      onCancel={onCancel}
      width={720}
      destroyOnClose
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0 truncate text-left text-xs">
            {selectedItem ? (
              <span className="text-[#6b7280]">
                已选：
                <span className="font-medium text-[#111827]">
                  {selectedItem.name}
                </span>
                <span className="ml-1 text-[#9ca3af]">
                  ({selectedItem.id})
                </span>
              </span>
            ) : (
              <span className="text-[#9ca3af]">尚未选择</span>
            )}
          </div>
          <div className="shrink-0 space-x-2">
            <Button onClick={onCancel}>取消</Button>
            <Button type="primary" onClick={handleConfirm}>
              确定
            </Button>
          </div>
        </div>
      }
    >
      {contextHolder}
      <div className="flex flex-col gap-3">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-[#bfbfbf]" />}
          placeholder="搜索知识库名称或描述"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
        />

        <div className="h-[480px] overflow-auto rounded-md border border-[#e5e7eb] bg-[#fafbfc] p-2">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spin />
            </div>
          ) : isError ? (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <span className="text-sm text-[#ef4444]">
                加载失败：{(error as Error)?.message || '未知错误'}
              </span>
              <Button size="small" onClick={() => refetch()}>
                重试
              </Button>
            </div>
          ) : visibleList.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={keyword ? '未搜到匹配的知识库' : '暂无知识库'}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {visibleList.map(renderCard)}
            </div>
          )}
        </div>

        <div className="text-xs text-[#9ca3af]">
          共 {list.length} 个知识库
          {keyword ? `，当前匹配 ${visibleList.length} 个` : ''}
        </div>
      </div>
    </Modal>
  );
};

export default KnowledgeBasePickerModal;
